import React from 'react';

// Skeleton Loader Component for better loading states
export const SkeletonLoader = ({ className = '', variant = 'default' }) => {
    const baseClasses = "animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] rounded";

    const variants = {
        default: 'h-4 w-full',
        card: 'h-32 w-full rounded-xl',
        stat: 'h-24 w-full rounded-lg',
        avatar: 'h-10 w-10 rounded-full',
        text: 'h-4 w-3/4',
        title: 'h-6 w-1/2',
        button: 'h-10 w-24 rounded-lg',
        badge: 'h-6 w-16 rounded-full'
    };

    return (
        <div
            className={`${baseClasses} ${variants[variant] || variants.default} ${className}`}
            style={{
                animation: 'skeleton-loading 1.5s ease-in-out infinite'
            }}
        />
    );
};

// Skeleton for Device Card
export const DeviceCardSkeleton = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 flex-1">
                <SkeletonLoader variant="avatar" />
                <div className="flex-1 space-y-2">
                    <SkeletonLoader variant="title" className="w-1/2" />
                    <SkeletonLoader variant="text" className="w-1/3" />
                </div>
            </div>
            <SkeletonLoader variant="badge" />
        </div>
        <div className="border-t border-gray-100 pt-4 space-y-2">
            <SkeletonLoader variant="text" className="w-full" />
            <SkeletonLoader variant="text" className="w-2/3" />
        </div>
        <div className="flex gap-2">
            <SkeletonLoader variant="button" className="flex-1" />
            <SkeletonLoader variant="button" className="w-20" />
        </div>
    </div>
);

// Skeleton for Stats Card
export const StatsCardSkeleton = () => (
    <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-6 space-y-3">
        <div className="flex items-center justify-between">
            <SkeletonLoader variant="text" className="w-24 bg-gray-300" />
            <SkeletonLoader variant="avatar" className="w-12 h-12 bg-gray-300" />
        </div>
        <SkeletonLoader variant="title" className="w-16 h-8 bg-gray-300" />
        <SkeletonLoader variant="text" className="w-32 bg-gray-300" />
    </div>
);

// Skeleton for Alert Item
export const AlertItemSkeleton = () => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-start gap-3">
            <SkeletonLoader variant="avatar" className="w-10 h-10" />
            <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between">
                    <SkeletonLoader variant="title" className="w-1/3" />
                    <SkeletonLoader variant="badge" />
                </div>
                <SkeletonLoader variant="text" className="w-full" />
                <SkeletonLoader variant="text" className="w-3/4" />
            </div>
        </div>
        <div className="flex gap-2">
            <SkeletonLoader variant="button" />
            <SkeletonLoader variant="button" />
        </div>
    </div>
);

// Skeleton for Table Row
export const TableRowSkeleton = ({ columns = 5 }) => (
    <tr className="border-b border-gray-100">
        {Array.from({ length: columns }).map((_, index) => (
            <td key={index} className="px-6 py-4">
                <SkeletonLoader variant="text" className={index === 0 ? 'w-full' : 'w-2/3'} />
            </td>
        ))}
    </tr>
);

// Complete Page Skeleton
export const PageSkeleton = ({ type = 'dashboard' }) => {
    if (type === 'dashboard') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-6 space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatsCardSkeleton />
                    <StatsCardSkeleton />
                    <StatsCardSkeleton />
                    <StatsCardSkeleton />
                </div>

                {/* Content Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-6 space-y-4">
                        <SkeletonLoader variant="title" className="w-1/3" />
                        <div className="space-y-3">
                            <AlertItemSkeleton />
                            <AlertItemSkeleton />
                            <AlertItemSkeleton />
                        </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 space-y-4">
                        <SkeletonLoader variant="title" className="w-1/3" />
                        <div className="space-y-3">
                            <AlertItemSkeleton />
                            <AlertItemSkeleton />
                            <AlertItemSkeleton />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (type === 'devices') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-6 space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatsCardSkeleton />
                    <StatsCardSkeleton />
                    <StatsCardSkeleton />
                    <StatsCardSkeleton />
                </div>

                {/* Device Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    <DeviceCardSkeleton />
                    <DeviceCardSkeleton />
                    <DeviceCardSkeleton />
                    <DeviceCardSkeleton />
                    <DeviceCardSkeleton />
                    <DeviceCardSkeleton />
                </div>
            </div>
        );
    }

    // Default skeleton
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-6 space-y-6">
            <div className="bg-white rounded-xl p-6 space-y-4">
                <SkeletonLoader variant="title" className="w-1/4" />
                <div className="space-y-2">
                    <SkeletonLoader variant="text" />
                    <SkeletonLoader variant="text" />
                    <SkeletonLoader variant="text" className="w-5/6" />
                </div>
            </div>
        </div>
    );
};

// Add CSS animation
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
        @keyframes skeleton-loading {
            0% {
                background-position: 200% 0;
            }
            100% {
                background-position: -200% 0;
            }
        }
    `;
    document.head.appendChild(style);
}

export default SkeletonLoader;
